/**
 * QuestionGroupEditorPage — group builder with branching + tree/preview tabs.
 * Faithful port of QuestionGroupEditorPage from screens-question-editors.jsx.
 */
import { useState, useEffect , type CSSProperties} from "react";
import { alpha, Btn, Card, Dropdown, Icon, IconBtn, Pill, T, Toggle } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { useQuestions, QTYPE, type Question, type QuestionGroup, type GroupItem } from "./useQuestions";
import { QGlyph } from "./QuestionsLibrary";
import { useT } from "../../lib/i18n";

const qbInput = (extra?: CSSProperties): CSSProperties => ({
  width: "100%", height: 40, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8,
  background: T.white, color: T.n1, fontSize: 13.5, fontFamily: T.font, boxSizing: "border-box", ...extra,
});

/* ── Approved question picker modal ───────────────────────────────────── */
function ApprovedQuestionPicker({ exclude, onPick, onClose }: { exclude: string[]; onPick: (q: Question) => void; onClose: () => void }) {
  const t = useT();
  const store = useQuestions();
  const [q, setQ] = useState("");
  const all = store.questions().filter((x) => !exclude.includes(x.id) && (x.status === "approved" || !q.trim() || x.label.toLowerCase().includes(q.toLowerCase())));
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 250, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: "94vw", maxHeight: "84vh", background: T.white, borderRadius: 14, boxShadow: `0 20px 50px -12px rgba(16,24,40,.4)`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: T.n1, margin: 0 }}>{t("questions.groupEditorPage.addAQuestion", "Add a question")}</h2>
          <IconBtn label={t("common.actions.close", "Close")} onClick={onClose}><Icon.x size={15}/></IconBtn>
        </div>
        <div style={{ padding: "10px 16px", borderBottom: `1px solid ${T.border}` }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("questions.groupEditorPage.searchApprovedQuestions", "Search approved questions…")}
            style={{ width: "100%", height: 36, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, fontFamily: T.font, fontSize: 13, color: T.n1, boxSizing: "border-box" }}/>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "8px 12px 12px" }}>
          {all.length === 0 ? <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("questions.groupEditorPage.noMatchingApprovedQuestions", "No matching approved questions.")}</div>
          : all.map((question) => (
            <button key={question.id} onClick={() => { onPick(question); onClose(); }}
              style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "10px 12px", border: "none", background: "transparent", borderRadius: 9, cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
              <span style={{ width: 30, height: 30, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><QGlyph type={question.type} size={15}/></span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{question.label}</div>
                <div style={{ fontSize: 11.5, color: T.soft }}>{QTYPE(question.type).label}</div>
              </div>
              <Icon.plus size={16} style={{ marginLeft: "auto", flexShrink: 0, color: T.soft }}/>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Mover arrows ────────────────────────────────────────────────────── */
function EntryMover({ i, n, move }: { i: number; n: number; move: (i: number, d: number) => void }) {
  const t = useT();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <IconBtn size={20} label={t("questions.groupEditorPage.moveUp", "Move up")}   onClick={() => move(i, -1)}><Icon.chev dir="up"   size={12}/></IconBtn>
      <IconBtn size={20} label={t("questions.groupEditorPage.moveDown", "Move down")} onClick={() => move(i, 1)}> <Icon.chev dir="down" size={12}/></IconBtn>
    </div>
  );
}

/* ── Tree view ──────────────────────────────────────────────────────── */
function QuestionTreeView({ group }: { group: QuestionGroup }) {
  const t = useT();
  const store = useQuestions();
  const items = group.items || [];
  if (items.length === 0) return <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("questions.groupEditorPage.noQuestionsYet9fc9f820", "No questions yet.")}</div>;
  return (
    <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 2 }}>
      {items.map((it, i) => {
        const q = store.getQ(it.qid); if (!q) return null;
        const rules = it.rules ? Object.entries(it.rules) : [];
        return (
          <div key={it.qid}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6 }}>
              <span style={{ fontSize: 11, color: T.soft, width: 16, textAlign: "center" }}>{i + 1}</span>
              <QGlyph type={q.type} size={13} color={T.soft}/>
              <span style={{ fontSize: 12.5, color: T.n1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{q.label}</span>
              {rules.length ? <span style={{ fontSize: 11, fontWeight: 600, color: T.p1 }}>{rules.length}{t("questions.groupEditorPage.branch", "×branch")}</span> : null}
            </div>
            {rules.map(([key, target]) => (
              <div key={key} style={{ marginLeft: 32, padding: "2px 8px", fontSize: 11, color: T.soft, display: "flex", alignItems: "center", gap: 6 }}>
                <Icon.arrow dir="right" size={10}/> <b>"{key}"</b> → {target === "__end__" ? t('questions.groupEditorPage.text') : (() => { const tq = store.getQ(target); return tq ? `Q${items.findIndex((x) => x.qid === target) + 1} · ${tq.label}` : target; })()}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/* ═══ Main editor ═══════════════════════════════════════════════════════ */
export default function QuestionGroupEditorPage() {
  const t = useT();
  const nav = useNav();
  const store = useQuestions();
  const isNew = !!(nav.params?.new);
  const viewOnly = !!(nav.params?.view);
  const id = nav.params?.id as string | undefined;
  const canWrite = !viewOnly && can("questions.write");

  const seed = (): QuestionGroup => isNew ? store.newGroup() : (id && store.getGroup(id) ? JSON.parse(JSON.stringify(store.getGroup(id))) : store.newGroup());
  const [form, setForm] = useState<QuestionGroup>(seed);
  const [picking, setPicking] = useState(false);
  const [openRules, setOpenRules] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState<"tree" | "preview">("tree");
  const routeKey = `${isNew ? "new" : id ?? ""}:${viewOnly ? "v" : "e"}`;
  useEffect(() => { setForm(seed()); }, [routeKey]);

  const items = form.items || [];
  const setItems = (next: GroupItem[] | ((a: GroupItem[]) => GroupItem[])) =>
    setForm((f) => ({ ...f, items: typeof next === "function" ? next(f.items || []) : next }));
  const addQuestion = (q: Question) => { if (items.some((it) => it.qid === q.id)) return; setItems((arr) => [...arr, { qid: q.id }]); };
  const removeItem = (qid: string) => setItems((arr) => arr.filter((it) => it.qid !== qid));
  const move = (i: number, dir: number) => setItems((arr) => { const j = i + dir; if (j < 0 || j >= arr.length) return arr; const n = arr.slice(); [n[i], n[j]] = [n[j], n[i]]; return n; });
  const setRequired = (qid: string, val: boolean) => setItems((arr) => arr.map((it) => it.qid === qid ? { ...it, required: val } : it));
  const setRule = (qid: string, key: string, target: string) => setItems((arr) => arr.map((it) => {
    if (it.qid !== qid) return it;
    const rules = { ...(it.rules || {}) };
    if (!target || target === "__next__") delete rules[key]; else rules[key] = target;
    return { ...it, rules };
  }));

  const branchKeys = (q: Question) => q.type === "yesno" ? [["yes","Yes"],["no","No"]] : (q.options || []).map((o) => [o.id, o.label]);
  const saveDisabled = !form.name.trim() || items.length === 0;
  const totalBranches = items.reduce((n, it) => n + (it.rules ? Object.keys(it.rules).length : 0), 0);

  const save = () => {
    store.upsertGroup({ ...form });
    toast({ tone: "success", title: isNew ? "Group created" : "Group saved", subtitle: t("questions.groupEditorPage.availableForUseInEvents", "Available for use in events.") });
    nav.go("questions.groups");
  };

  return (
    <Screen rail="cs-questions">
      <PageHeader
        title={isNew ? t("questions.questionGroupEditorPage.newQuestionGroup", "New question group") : (viewOnly ? form.name || t("questions.library.group", "Group") : t("questions.questionGroupEditorPage.editFormNameGroup", { value0: form.name || t("questions.library.group", "Group") }))}
        subtitle={<span>{items.length} {t("questions.groupEditorPage.question", "question")}{" "}{items.length === 1 ? "" : t('questions.groupEditorPage.text2')}{" "}{totalBranches ? /* TODO(i18n): replace template-literal with t('questions.groupEditorPage.text3', { ... }) */` · ${totalBranches} branch${totalBranches === 1 ? "" : "es"}` : ""}</span>}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("questions.groups")}><Icon.arrow dir="left" size={11}/> {t("questions.groupEditorPage.groups", "Groups")}</span>}
        actions={viewOnly
          ? <Btn icon={<Icon.edit size={14}/>} onClick={() => nav.go("questions.group.edit", { id: form.id })}>{t("common.actions.edit", "Edit")}</Btn>
          : <>
              <Btn variant="secondary" onClick={() => nav.go("questions.groups")}>{t("common.actions.cancel", "Cancel")}</Btn>
              <Btn icon={<Icon.check size={14}/>} disabled={saveDisabled} onClick={save}>{isNew ? t("questions.questionGroupEditorPage.createGroup", "Create group") : t("common.actions.save", "Save")}</Btn>
            </>}
      />
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(460px,1fr) minmax(380px,520px)", minHeight: 0, borderTop: `1px solid ${T.border}`, marginTop: 16 }}>
        {/* Builder panel */}
        <div style={{ borderRight: `1px solid ${T.border}`, overflow: "auto", padding: "20px 24px 40px", background: T.canvas, opacity: canWrite ? 1 : 0.75, pointerEvents: canWrite ? "auto" : "none" }}>
          <Card style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
            <div>{(() => <div style={{ fontSize: 12, fontWeight: 600, color: T.n2, marginBottom: 5 }}>{t("questions.groupEditorPage.groupName", "Group name")}<span style={{ color: T.red1, marginLeft: 3 }}>*</span></div>)()}<input autoFocus={isNew} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t("questions.groupEditorPage.eGStandardRegistration", "e.g. Standard registration")} style={qbInput()}/></div>
            <div><div style={{ fontSize: 12, fontWeight: 600, color: T.n2, marginBottom: 5 }}>{t("questions.groupEditorPage.description", "Description")}</div><input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={t("questions.groupEditorPage.whenToUseThisGroup", "When to use this group")} style={qbInput()}/></div>
          </Card>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 2px 12px" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("questions.groupEditorPage.questionsInThisGroup", "Questions in this group")}</div>
              <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{t("questions.groupEditorPage.orderedTopBottomAddBranchingOnChoiceQuestions", "Ordered top→bottom. Add branching on choice questions.")}</div>
            </div>
            <Btn variant="secondary" size="sm" icon={<Icon.plus size={13}/>} onClick={() => setPicking(true)}>{t("questions.groupEditorPage.addQuestion", "Add question")}</Btn>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((it, i) => {
              const q = store.getQ(it.qid); if (!q) return null;
              const reqEffective = it.required != null ? it.required : q.required;
              const canBranch = QTYPE(q.type).branch;
              const open = openRules === it.qid;
              const ruleCount = it.rules ? Object.keys(it.rules).length : 0;
              return (
                <Card key={it.qid} style={{ padding: 0, overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px" }}>
                    <EntryMover i={i} n={items.length} move={move}/>
                    <span style={{ width: 24, height: 24, borderRadius: 999, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                    <QGlyph type={q.type} size={15} color={T.p1}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{q.label}</div>
                      <div style={{ fontSize: 11.5, color: T.soft }}>{QTYPE(q.type).label}{ruleCount ? /* TODO(i18n): replace template-literal with t('questions.groupEditorPage.text4', { ... }) */` · ${ruleCount} branch${ruleCount === 1 ? "" : "es"}` : ""}</div>
                    </div>
                    <label onClick={() => setRequired(it.qid, !reqEffective)} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                      <Toggle on={!!reqEffective}/><span style={{ fontSize: 12, color: T.soft }}>{t("questions.groupEditorPage.req", "Req")}</span>
                    </label>
                    {canBranch ? <IconBtn size={30} label={t("questions.groupEditorPage.branching", "Branching")} onClick={() => setOpenRules(open ? null : it.qid)} style={{ color: ruleCount ? T.p1 : T.n2 }}><Icon.network size={15}/></IconBtn> : null}
                    <IconBtn size={30} label={t("common.actions.remove", "Remove")} onClick={() => removeItem(it.qid)}><Icon.trash size={14}/></IconBtn>
                  </div>
                  {canBranch && open ? (
                    <div style={{ borderTop: `1px solid ${T.border}`, background: T.p4, padding: "12px 14px 14px" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.p1, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}><Icon.network size={13}/> {t("questions.groupEditorPage.branchingWhenAnswered", "Branching — when answered…")}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                        {branchKeys(q).map(([key, label]) => {
                          const cur = (it.rules || {})[key] || "__next__";
                          const laterQs = items.slice(i + 1);
                          return (
                            <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 12.5, color: T.n1, minWidth: 110, fontWeight: 600 }}>"{label}"</span>
                              <Icon.arrow dir="right" size={12}/>
                              <Dropdown
                                options={[
                                  { value: "__next__", label: t("questions.groupEditorPage.continueInOrder", "Continue in order") },
                                  { value: "__end__", label: t("questions.groupEditorPage.endTheForm", "End the form") },
                                  ...laterQs.map((li, k) => { const lq = store.getQ(li.qid); return lq ? { value: li.qid, label: `${t("questions.groupEditorPage.skipToQ", "Skip to Q")} ${i + 2 + k} · ${lq.label}` } : null; }).filter((o): o is { value: string; label: string } => !!o),
                                ]}
                                value={cur}
                                onChange={(v) => setRule(it.qid, key, v)}
                                aria-label={t("questions.groupEditorPage.branchTarget", "Branch target")}
                                style={{ gap: 0, flex: 1 }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </Card>
              );
            })}
            {items.length === 0 ? (
              <div style={{ padding: 30, border: `1.5px dashed ${T.n4}`, borderRadius: 10, textAlign: "center", background: T.white }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{t("questions.groupEditorPage.noQuestionsYet", "No questions yet")}</div>
                <div style={{ fontSize: 12.5, color: T.soft, margin: "4px 0 12px" }}>{t("questions.groupEditorPage.addApprovedQuestionsToBuildTheFlow", "Add approved questions to build the flow.")}</div>
                <Btn size="sm" icon={<Icon.plus size={13}/>} onClick={() => setPicking(true)}>{t("questions.groupEditorPage.addQuestion", "Add question")}</Btn>
              </div>
            ) : null}
          </div>
        </div>

        {/* Right panel — tree / preview tabs */}
        <div style={{ overflow: "hidden", background: T.canvas, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div style={{ display: "flex", gap: 4, padding: "14px 16px 0" }}>
            {([["tree","Flow tree",<Icon.network size={13}/>],["preview","Question preview",<Icon.eye size={13}/>]] as const).map(([tid, lbl, ic]) => (
              <button key={tid} onClick={() => setRightTab(tid as "tree"|"preview")}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "none", background: rightTab === tid ? T.white : "transparent", color: rightTab === tid ? T.p1 : T.n2, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: T.font }}>
                {ic}{lbl}
              </button>
            ))}
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: 16, minHeight: 0 }}>
            {rightTab === "tree"
              ? <Card style={{ padding: 0 }}><QuestionTreeView group={form}/></Card>
              : <div style={{ fontSize: 13, color: T.soft, textAlign: "center", paddingTop: 40 }}>{t("questions.groupEditorPage.respondentPreviewRequiresAtLeastOneQuestion", "Respondent preview requires at least one question.")}</div>}
          </div>
        </div>
      </div>
      {picking ? <ApprovedQuestionPicker exclude={items.map((it) => it.qid)} onPick={addQuestion} onClose={() => setPicking(false)}/> : null}
    </Screen>
  );
}
